import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalHeader, ModalFooter, Label, Input } from 'reactstrap';
import { Switch } from '../buttons/Swtich';
import { FieldSensitivityService } from '../../utils/FieldSensitivityService';
import { APIClient } from '../../utils/APIClient';
import { API_URL } from '../../settings/local_settings';
import { Spinner } from '../Spinner';

class FieldSensitivityModalComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            agency: null,
            user: null,
            isUserAdmin: false,
            useCustomSensitivity: false,
            agencyValue1: '',
            agencyValue2: '',
            userValue1: '',
            userValue2: '',
            saving: false,
            error: null,
            showSensitivityColor: true, // Por defecto siempre es TRUE
        };
        this.handleAgencyValue1Change = this.handleAgencyValue1Change.bind(this);
        this.handleAgencyValue2Change = this.handleAgencyValue2Change.bind(this);
        this.handleUserValue1Change = this.handleUserValue1Change.bind(this);
        this.handleUserValue2Change = this.handleUserValue2Change.bind(this);
        this.handleToggleCustomSensitivity = this.handleToggleCustomSensitivity.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.loadSensitivityData = this.loadSensitivityData.bind(this);
        this.checkUserIsAdmin = this.checkUserIsAdmin.bind(this);
        this.handleToggleShowSensitivityColor = this.handleToggleShowSensitivityColor.bind(this);
    }

    componentDidMount() {
        this.loadSensitivityData();
        this.checkUserIsAdmin();
    }

    componentDidUpdate(prevProps) {
        // Recargar datos cuando cambia el fieldId o cuando el modal se abre
        if (prevProps.fieldId !== this.props.fieldId || (prevProps.isOpen !== this.props.isOpen && this.props.isOpen)) {
            if (this.props.isOpen) {
                this.loadSensitivityData();
                this.checkUserIsAdmin();
            }
        }
    }

    async checkUserIsAdmin() {
        try {
            const userId = localStorage.getItem('logged_user_id');
            if (!userId) {
                this.setState({ isUserAdmin: false });
                return;
            }
            
            const response = await new APIClient(`${API_URL}user/${userId}/`).call('GET');
            if (response.status === 'OK') {
                this.setState({ 
                    isUserAdmin: response.data.user_doing_operation_is_admin || false 
                });
            }
        } catch (error) {
            console.error('Error checking admin status:', error);
            this.setState({ isUserAdmin: false });
        }
    }

    async loadSensitivityData() {
        const { fieldId } = this.props;
        if (!fieldId) return;

        this.setState({ loading: true, error: null });

        try {
            const data = await FieldSensitivityService.getFieldSensitivity(fieldId);
            
            const agency = data.agency || {};
            const user = data.user || null;
            
            // Determinar show_sensitivity_color: si hay user, usar el del user, sino el de agency, sino true por defecto
            const showSensitivityColor = user !== null 
                ? (user.show_sensitivity_color !== undefined ? user.show_sensitivity_color : true)
                : (agency.show_sensitivity_color !== undefined ? agency.show_sensitivity_color : true);
            
            this.setState({
                loading: false,
                agency: agency,
                user: user,
                useCustomSensitivity: user !== null && (user.sensitivity_value_1 !== null || user.sensitivity_value_2 !== null),
                agencyValue1: agency.sensitivity_value_1 || '',
                agencyValue2: agency.sensitivity_value_2 || '',
                userValue1: user && user.sensitivity_value_1 !== null ? user.sensitivity_value_1 : (agency.sensitivity_value_1 || ''),
                userValue2: user && user.sensitivity_value_2 !== null ? user.sensitivity_value_2 : (agency.sensitivity_value_2 || ''),
                showSensitivityColor: showSensitivityColor,
            });
        } catch (error) {
            console.error('Error loading sensitivity data:', error);
            this.setState({ 
                loading: false, 
                error: this.props.t('data_explorer.error_loading_sensitivity', 'Error al cargar la sensibilidad')
            });
        }
    }

    handleAgencyValue1Change(event) {
        this.setState({ agencyValue1: event.target.value });
    }

    handleAgencyValue2Change(event) {
        this.setState({ agencyValue2: event.target.value });
    }

    handleUserValue1Change(event) {
        this.setState({ userValue1: event.target.value });
    }

    handleUserValue2Change(event) {
        this.setState({ userValue2: event.target.value });
    }

    handleToggleCustomSensitivity(event) {
        const useCustom = event.target.checked;
        this.setState({ 
            useCustomSensitivity: useCustom,
            // Si se activa, inicializar con valores de agencia si no hay valores de usuario
            userValue1: useCustom ? (this.state.userValue1 || this.state.agencyValue1) : this.state.agencyValue1,
            userValue2: useCustom ? (this.state.userValue2 || this.state.agencyValue2) : this.state.agencyValue2,
        });
    }

    handleToggleShowSensitivityColor(event) {
        const enabled = event.target.checked;
        this.setState({ showSensitivityColor: enabled });
    }

    async handleSave() {
        const { fieldId, onSave } = this.props;
        const { 
            agencyValue1, 
            agencyValue2, 
            userValue1, 
            userValue2, 
            useCustomSensitivity,
            isUserAdmin,
            showSensitivityColor
        } = this.state;

        this.setState({ saving: true, error: null });

        try {
            // Si el usuario es admin y está editando valores de agencia
            if (!useCustomSensitivity && isUserAdmin) {
                const value1 = parseFloat(agencyValue1);
                const value2 = parseFloat(agencyValue2);
                
                if (isNaN(value1) || isNaN(value2) || value1 < 0 || value2 < 0) {
                    alert(this.props.t('data_explorer.invalid_sensitivity_values', 'Los valores deben ser números positivos'));
                    this.setState({ saving: false });
                    return;
                }

                if (value1 >= value2) {
                    alert(this.props.t('data_explorer.sensitivity_value1_must_be_less', 'El primer valor debe ser menor que el segundo'));
                    this.setState({ saving: false });
                    return;
                }
                
                await FieldSensitivityService.updateSensitivity(fieldId, value1, value2, false, showSensitivityColor);
            }
            
            // Manejo de sensibilidad a nivel usuario
            if (useCustomSensitivity) {
                // Usuario quiere configurar su propia sensibilidad
                const value1 = parseFloat(userValue1);
                const value2 = parseFloat(userValue2);
                
                if (isNaN(value1) || isNaN(value2) || value1 < 0 || value2 < 0) {
                    alert(this.props.t('data_explorer.invalid_sensitivity_values', 'Los valores deben ser números positivos'));
                    this.setState({ saving: false });
                    return;
                }

                if (value1 >= value2) {
                    alert(this.props.t('data_explorer.sensitivity_value1_must_be_less', 'El primer valor debe ser menor que el segundo'));
                    this.setState({ saving: false });
                    return;
                }
                
                await FieldSensitivityService.updateSensitivity(fieldId, value1, value2, true, showSensitivityColor);
            } else {
                // Usuario NO quiere configurar su propia sensibilidad
                // Si había configuración de usuario previa, verificar si debemos borrarla
                const hadUserConfig = this.state.user !== null;
                
                if (showSensitivityColor) {
                    // Caso especial según requerimiento: showSensitivityColor=true, useCustomSensitivity=false
                    // Si había configuración de usuario (con valores o solo show_sensitivity_color), borrarla para usar valores de agencia
                    if (hadUserConfig) {
                        await FieldSensitivityService.deleteUserFieldSensitivity(fieldId);
                    }
                } else {
                    await FieldSensitivityService.updateSensitivity(fieldId, null, null, true, false);
                }
            }

            this.setState({ saving: false });
            
            // Recargar datos para asegurar que el estado esté actualizado
            await this.loadSensitivityData();
            
            if (onSave) {
                onSave();
            }
            
            this.props.toggle();
        } catch (error) {
            console.error('Error saving sensitivity:', error);
            this.setState({ 
                saving: false, 
                error: error.message || this.props.t('data_explorer.error_saving_sensitivity', 'Error al guardar la sensibilidad')
            });
        }
    }

    getIndicatorBehavior() {
        const { fieldId, fieldsData } = this.props;
        if (!fieldsData || !fieldId) return null;
        
        const fieldData = fieldsData.FIELD_DATA_BY_ID[fieldId];
        return fieldData ? fieldData.indicator_behavior : null;
    }

    getLabelText(valueType) {
        const { t } = this.props;

        // Retornar solo la parte base del texto sin la palabra BUENO/MUY BUENO/MALO/CRÍTICO
        return t('data_explorer.show_as_value', 'Mostrar como un valor');
    }

    getBadgeText(valueType) {
        const indicatorBehavior = this.getIndicatorBehavior();
        
        if (indicatorBehavior === 'LESS_IS_BETTER') {
            return valueType === 1 ? 'MALO' : 'CRÍTICO';
        } else {
            return valueType === 1 ? 'BUENO' : 'MUY BUENO';
        }
    }

    getColorClass(valueType) {
        const indicatorBehavior = this.getIndicatorBehavior();

        if (indicatorBehavior === 'LESS_IS_BETTER') {
            return valueType === 1 ? 'bad' : 'critic';
        } else {
            return valueType === 1 ? 'good' : 'very-good';
        }
    }

    getMirrorBadgeText(valueType) {
        const indicatorBehavior = this.getIndicatorBehavior();

        if (indicatorBehavior === 'LESS_IS_BETTER') {
            return valueType === 1 ? 'BUENO' : 'MUY BUENO';
        } else {
            return valueType === 1 ? 'MALO' : 'CRÍTICO';
        }
    }

    getMirrorColorClass(valueType) {
        const indicatorBehavior = this.getIndicatorBehavior();

        if (indicatorBehavior === 'LESS_IS_BETTER') {
            return valueType === 1 ? 'good' : 'very-good';
        } else {
            return valueType === 1 ? 'bad' : 'critic';
        }
    }

    render() {
        const { t, isOpen, toggle, fieldId, fieldsData } = this.props;
        const { 
            loading, 
            agencyValue1, 
            agencyValue2, 
            userValue1, 
            userValue2, 
            useCustomSensitivity,
            isUserAdmin,
            saving,
            error,
            showSensitivityColor
        } = this.state;

        if (!fieldId || !fieldsData) {
            return null;
        }

        const fieldData = fieldsData.FIELD_DATA_BY_ID[fieldId];
        const fieldName = fieldData ? fieldData.name : fieldId;

        return (
            <Modal isOpen={isOpen} toggle={toggle} size="lg" className="field-sensitivity-modal">
                <ModalHeader toggle={toggle}>
                    {t('data_explorer.configure_sensitivity', 'Configurar Sensibilidad')} - {fieldName}
                </ModalHeader>
                <ModalBody style={{ padding: '2em' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            {/* Sensibilidad de Agencia */}
                            <div style={{ marginBottom: '30px' }}>
                                <h5>{t('data_explorer.agency_sensitivity', 'Sensibilidad de la Agencia')}</h5>

                                <div style={{ marginBottom: '8px' }}>
                                    <Label>
                                        {this.getLabelText(1)}{' '}
                                        <span className={`badge badge-${this.getColorClass(1)}`} style={{ padding: '2px 8px' }}>
                                            {this.getBadgeText(1)}
                                        </span>
                                        {' '}
                                        {t('data_explorer.if_exceeds', 'si supera el')} {' '}
                                        <Input
                                            type="number"
                                            value={agencyValue1}
                                            onChange={this.handleAgencyValue1Change}
                                            disabled={!isUserAdmin}
                                            style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', height: 'auto', lineHeight: '1.2' }}
                                            min="0"
                                            step="0.1"
                                        />
                                        % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                    </Label>
                                </div>

                                <div style={{ marginBottom: '8px' }}>
                                    <Label>
                                        {this.getLabelText(2)}{' '}
                                        <span className={`badge badge-${this.getColorClass(2)}`} style={{ padding: '2px 8px' }}>
                                            {this.getBadgeText(2)}
                                        </span>
                                        {' '}
                                        {t('data_explorer.if_exceeds', 'si supera el')} {' '}
                                        <Input
                                            type="number"
                                            value={agencyValue2}
                                            onChange={this.handleAgencyValue2Change}
                                            disabled={!isUserAdmin}
                                            style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', height: 'auto', lineHeight: '1.2' }}
                                            min="0"
                                            step="0.1"
                                        />
                                        % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                    </Label>
                                </div>

                                {/* Filas espejo informativas */}
                                <div style={{ marginBottom: '8px', opacity: 0.7 }}>
                                    <Label>
                                        {this.getLabelText(1)}{' '}
                                        <span className={`badge badge-${this.getMirrorColorClass(1)}`} style={{ padding: '2px 8px' }}>
                                            {this.getMirrorBadgeText(1)}
                                        </span>
                                        {' '}
                                        {t('data_explorer.if_drops_more_than', 'si baja más del')} {' '}
                                        <span style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                                            {agencyValue1 || '—'}
                                        </span>
                                        % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                    </Label>
                                </div>

                                <div style={{ marginBottom: '8px', opacity: 0.7 }}>
                                    <Label>
                                        {this.getLabelText(2)}{' '}
                                        <span className={`badge badge-${this.getMirrorColorClass(2)}`} style={{ padding: '2px 8px' }}>
                                            {this.getMirrorBadgeText(2)}
                                        </span>
                                        {' '}
                                        {t('data_explorer.if_drops_more_than', 'si baja más del')} {' '}
                                        <span style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                                            {agencyValue2 || '—'}
                                        </span>
                                        % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                    </Label>
                                </div>

                                {!isUserAdmin && (
                                    <p className="text-muted" style={{ fontSize: '0.9em' }}>
                                        {t('data_explorer.only_admin_can_edit_agency', 'Solo los administradores pueden editar la sensibilidad de la agencia')}
                                    </p>
                                )}
                            </div>

                            {/* Sensibilidad del Usuario - Título siempre visible */}
                            <div style={{ marginBottom: '30px' }}>
                                <h5>{t('data_explorer.user_sensitivity', 'Sensibilidad del Usuario')}</h5>
                                
                                {/* Toggle para alertas por color */}
                                <div 
                                    style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', display: 'flex', alignItems: 'center' }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <div 
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', height: '22px' }}>
                                            <Switch
                                                name="showSensitivityColor"
                                                checked={showSensitivityColor}
                                                onChange={this.handleToggleShowSensitivityColor}
                                                style={{ margin: 0, marginLeft: 0 }}
                                            />
                                        </div>
                                    </div>
                                    <Label 
                                        style={{ marginLeft: '10px', marginBottom: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleToggleShowSensitivityColor({ target: { checked: !showSensitivityColor } });
                                        }}
                                    >
                                        {t('data_explorer.color_alerts_active_in_column', 'Alertas por color activas en esta columna')}
                                    </Label>
                                </div>

                                {/* Toggle para sensibilidad personalizada */}
                                <div 
                                    style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', display: 'flex', alignItems: 'center' }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <div 
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', height: '22px' }}>
                                            <Switch
                                                name="useCustomSensitivity"
                                                checked={useCustomSensitivity}
                                                onChange={this.handleToggleCustomSensitivity}
                                                style={{ margin: 0, marginLeft: 0 }}
                                            />
                                        </div>
                                    </div>
                                    <Label 
                                        style={{ marginLeft: '10px', marginBottom: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleToggleCustomSensitivity({ target: { checked: !useCustomSensitivity } });
                                        }}
                                    >
                                        {t('data_explorer.configure_my_own_sensitivity', 'Quiero configurar mi propia sensibilidad')}
                                    </Label>
                                </div>

                                {/* Sensibilidad Personalizada del Usuario */}
                                {useCustomSensitivity && (
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Label>
                                                {this.getLabelText(1)}{' '}
                                                <span className={`badge badge-${this.getColorClass(1)}`} style={{ padding: '2px 8px' }}>
                                                    {this.getBadgeText(1)}
                                                </span>
                                                {' '}
                                                {t('data_explorer.if_exceeds', 'si supera el')} {' '}
                                                <Input
                                                    type="number"
                                                    value={userValue1}
                                                    onChange={this.handleUserValue1Change}
                                                    style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', height: 'auto', lineHeight: '1.2' }}
                                                    min="0"
                                                    step="0.1"
                                                />
                                                % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                            </Label>
                                        </div>

                                        <div style={{ marginBottom: '8px' }}>
                                            <Label>
                                                {this.getLabelText(2)}{' '}
                                                <span className={`badge badge-${this.getColorClass(2)}`} style={{ padding: '2px 8px' }}>
                                                    {this.getBadgeText(2)}
                                                </span>
                                                {' '}
                                                {t('data_explorer.if_exceeds', 'si supera el')} {' '}
                                                <Input
                                                    type="number"
                                                    value={userValue2}
                                                    onChange={this.handleUserValue2Change}
                                                    style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', height: 'auto', lineHeight: '1.2' }}
                                                    min="0"
                                                    step="0.1"
                                                />
                                                % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                            </Label>
                                        </div>

                                        {/* Filas espejo informativas */}
                                        <div style={{ marginBottom: '8px', opacity: 0.7 }}>
                                            <Label>
                                                {this.getLabelText(1)}{' '}
                                                <span className={`badge badge-${this.getMirrorColorClass(1)}`} style={{ padding: '2px 8px' }}>
                                                    {this.getMirrorBadgeText(1)}
                                                </span>
                                                {' '}
                                                {t('data_explorer.if_drops_more_than', 'si baja más del')} {' '}
                                                <span style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                                                    {userValue1 || '—'}
                                                </span>
                                                % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                            </Label>
                                        </div>

                                        <div style={{ marginBottom: '8px', opacity: 0.7 }}>
                                            <Label>
                                                {this.getLabelText(2)}{' '}
                                                <span className={`badge badge-${this.getMirrorColorClass(2)}`} style={{ padding: '2px 8px' }}>
                                                    {this.getMirrorBadgeText(2)}
                                                </span>
                                                {' '}
                                                {t('data_explorer.if_drops_more_than', 'si baja más del')} {' '}
                                                <span style={{ display: 'inline-block', width: '80px', margin: '0 5px', padding: '0.25rem 0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                                                    {userValue2 || '—'}
                                                </span>
                                                % {t('data_explorer.compared_to_period', 'respecto al periodo comparativo')}
                                            </Label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <button 
                        className="btn -secondary" 
                        onClick={toggle}
                        disabled={saving}
                    >
                        {t('common.cancel', 'Cancelar')}
                    </button>
                    <button 
                        className="btn -primary" 
                        onClick={this.handleSave}
                        disabled={saving || loading}
                    >
                        {saving ? t('common.saving', 'Guardando...') : t('common.save', 'Guardar')}
                    </button>
                </ModalFooter>
            </Modal>
        );
    }
}

export const FieldSensitivityModal = withTranslation()(FieldSensitivityModalComponent);

